/**
 * "Reseller Accounts" — the `/admin/resellers` tab of System Admin.
 *
 * XOP staff create reseller COMPANIES in the CRM (Administration ▸ Resellers,
 * which already owns that form and now carries the parent + billing-identity
 * fields too). This page owns the other half: the PEOPLE inside those
 * companies — list, invite, change role, revoke.
 *
 * Why it lives here and not in the CRM tab: inviting a reseller user is an
 * IDENTITY operation, and identity is System Admin's job in this stack (the
 * Dashboard Users panel next door is the same shape). It is also the only
 * surface in the app that writes to crm-api AND portal-api in one gesture.
 *
 * THE TWO-SIDED WRITE, and why this page cares. A reseller user is a CRM
 * binding (crm-api `ResellerMembership`) plus a sign-in account (portal-api
 * `User{role:"reseller"}`). The server writes crm-api first and portal-api
 * second so a partial failure DENIES access rather than granting an unscoped
 * one — see `apps/server/src/routes/resellerAccounts.ts`. What that means HERE
 * is that a mutation can succeed and still be half-done, so every handler below
 * reads the response BODY (`invited`, `account_disabled`) rather than treating
 * "no exception" as "it worked", and the roster re-derives a per-row verdict
 * from both sides so a half-write is still visible after a reload.
 *
 * Gate: `crm:resellers` (which super_admin holds by definition) — the same
 * permission the CRM reseller wall uses.
 */
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import {
  INVITE_ERROR_EMAIL_IS_STAFF,
  type ListResellerMembersResponse,
  type ResellerMembership,
  type ResellerRoleKey,
  type ResellerRoleMatrix,
} from "@xop/dashboard-shared";
import { UserPlus } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageLoader } from "@/components/ui/page-loader";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { headCellSx } from "@/pages/admin/dashboard-users/tableStyles";
import usePermissions from "@crm-src/hooks/usePermissions";
import { InviteResellerMemberDialog } from "./accounts/InviteResellerMemberDialog.js";
import { ResellerMemberRow } from "./accounts/ResellerMemberRow.js";
import { ResellerPermissionsDrawer } from "./accounts/ResellerPermissionsDrawer.js";
import { RoleMatrixPanel } from "./accounts/RoleMatrixPanel.js";
import {
  resellerMatrixKey,
  resellerMembersKey,
  useResellerMemberMutations,
} from "./accounts/useResellerMemberMutations.js";

export function ResellerAccountsTab({
  resellerId,
  resellerName,
}: {
  resellerId: string;
  resellerName: string;
}): JSX.Element {
  const { canManageResellers } = usePermissions();
  const canManage = canManageResellers();
  const selectedId = Number(resellerId);
  const selected = { id: selectedId, name: resellerName };

  const rosterQuery = useQuery({
    queryKey: resellerMembersKey(selectedId),
    queryFn: () =>
      api<ListResellerMembersResponse>(`/api/admin/resellers/${selectedId}/members`),
    enabled: selectedId !== null,
  });

  const matrixQuery = useQuery({
    queryKey: resellerMatrixKey(selectedId),
    queryFn: () => api<ResellerRoleMatrix>(`/api/admin/resellers/${selectedId}/role-permissions`),
    enabled: selectedId !== null,
  });

  const { invite, setRole, revoke, remove, resendInvite, setPermissions, setMatrix, resetMatrix, createRole } =
    useResellerMemberMutations(selectedId);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<{ id: number; email: string } | null>(
    null,
  );
  const [pendingDelete, setPendingDelete] = useState<{ id: number; email: string } | null>(null);
  const [drawerFor, setDrawerFor] = useState<ResellerMembership | null>(null);
  const [drawerError, setDrawerError] = useState<string | null>(null);

  const members = rosterQuery.data?.members ?? [];
  const orphanedAccounts = rosterQuery.data?.orphaned_accounts ?? [];
  const identityAvailable = rosterQuery.data?.identity_available ?? true;

  return (
    <Box sx={{ display: "grid", gap: 3 }} data-cy="reseller-accounts-tab">
      {matrixQuery.data && matrixQuery.data.roles && (
        <RoleMatrixPanel
          companyName={selected.name}
          matrix={matrixQuery.data}
          saving={setMatrix.isPending || resetMatrix.isPending || createRole.isPending}
          readOnly={!canManage}
          onCreate={(name) => createRole.mutateAsync(name)}
          onRename={(role, name, permissions) => setMatrix.mutateAsync({ role, permissions, name })}
          onDelete={async (role) => {
            await resetMatrix.mutateAsync(role);
            toast({ title: "Role deleted", severity: "success" });
          }}
          onChange={(role, permissions) =>
            setMatrix.mutate(
              { role, permissions },
              {
                onError: (e) =>
                  toast({
                    title: "Couldn't change the role",
                    description: e instanceof ApiError ? e.message : "The CRM was unreachable.",
                    severity: "error",
                  }),
              },
            )
          }
          onReset={(role) => resetMatrix.mutate(role)}
        />
      )}

      <Card>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderBottom: 1,
              borderColor: "divider",
              px: 2.5,
              py: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
                {selected.name} — users
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                External accounts. They see only this reseller&rsquo;s own customers, never XOP-direct ones.
              </Typography>
            </Box>
            {canManage && (
              <Button
                variant="contained"
                size="small"
                startIcon={<UserPlus size={14} />}
                onClick={() => setInviteOpen(true)}
                data-cy="reseller-invite-user"
              >
                Invite user
              </Button>
            )}
          </Box>

          {!identityAvailable && (
            <Box
              role="status"
              sx={{
                mx: 2.5,
                mt: 2,
                borderRadius: 1,
                border: 1,
                borderColor: (t) => alpha(t.palette.warning.main, 0.4),
                bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
                px: 1.5,
                py: 1,
                fontSize: 13,
              }}
              data-cy="reseller-identity-unavailable"
            >
              {/* Sign-in accounts that could not be READ are NOT orphaned
                  accounts. The server refuses to report them as such, and this
                  says why the sign-in columns are incomplete instead of leaving
                  the operator to conclude that nobody has an account. */}
              Sign-in status couldn&rsquo;t be loaded from the support portal right now, so the
              Status and Last sign-in columns may be incomplete. The roles below come from the CRM
              and are accurate.
            </Box>
          )}

          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            {rosterQuery.isLoading && <PageLoader />}
            {rosterQuery.isError && (
              <Box sx={{ p: 2.5 }}>
                <ErrorNotice
                  title="Couldn't load this reseller's users."
                  detail={
                    rosterQuery.error instanceof ApiError
                      ? rosterQuery.error.message
                      : "Unknown error from the reseller accounts API."
                  }
                  onRetry={() => void rosterQuery.refetch()}
                />
              </Box>
            )}
            {rosterQuery.data && (
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ "& td, & th": { fontSize: 14 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>User</TableCell>
                      <TableCell sx={headCellSx}>Role</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell>
                      <TableCell sx={headCellSx}>Last sign-in</TableCell>
                      <TableCell sx={[headCellSx, { textAlign: "right" }]}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((member) => (
                      <ResellerMemberRow
                        key={member.membership.id}
                        member={member}
                        matrix={matrixQuery.data}
                        canManage={canManage}
                        onRoleChange={(role: ResellerRoleKey) => {
                          setRole.mutate(
                            { id: member.membership.id, role },
                            {
                              onError: (e) =>
                                toast({
                                  title: "Couldn't change the role",
                                  description:
                                    e instanceof ApiError ? e.message : "The CRM was unreachable.",
                                  severity: "error",
                                }),
                            },
                          );
                        }}
                        onPermissions={() => {
                          setDrawerError(null);
                          setDrawerFor(member.membership);
                        }}
                        onResendInvite={() => {
                          void (async () => {
                            try {
                              await resendInvite.mutateAsync(member.membership.id);
                              toast({
                                title: "Invite sent",
                                description: `A set-password link was emailed to ${member.membership.email}.`,
                                severity: "success",
                              });
                            } catch (e) {
                              toast({
                                title: "Couldn't send the invite",
                                description:
                                  e instanceof ApiError
                                    ? e.message
                                    : "The support portal couldn't be reached.",
                                severity: "error",
                              });
                            }
                          })();
                        }}
                        onRevoke={() =>
                          setPendingRevoke({
                            id: member.membership.id,
                            email: member.membership.email,
                          })
                        }
                        onDelete={() =>
                          setPendingDelete({
                            id: member.membership.id,
                            email: member.membership.email,
                          })
                        }
                      />
                    ))}
                    {members.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{ py: 3, textAlign: "center", fontSize: 14, color: "text.disabled" }}
                        >
                          Nobody at {selected.name} has access yet. Use{" "}
                          <Box component="span" sx={{ fontWeight: 500 }}>Invite user</Box> to add
                          their first account.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>

          {orphanedAccounts.length > 0 && (
            <Box sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: "divider" }} data-cy="reseller-orphaned-accounts">
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "warning.main", mb: 0.5 }}>
                Reseller sign-ins that belong to no company
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {/* These grant nothing — they authenticate and then fail
                    resolution — but they are loose ends worth closing. Only
                    super admins get this list, and it never names another
                    company's users (BFF `unboundResellerAccounts`). */}
                These accounts can sign in but belong to no reseller company, so they see nothing.
                They are left over from a removed company or membership:{" "}
                {orphanedAccounts.map((o) => o.account.email).join(", ")}.
              </Typography>
            </Box>
          )}
      </Card>

      <ResellerPermissionsDrawer
        member={drawerFor}
        matrix={matrixQuery.data}
        matrixUnavailable={matrixQuery.isError}
        open={drawerFor !== null}
        saving={setPermissions.isPending}
        error={drawerError}
        onClose={() => setDrawerFor(null)}
        onSave={(next) =>
          drawerFor &&
          setPermissions.mutate(
            { id: drawerFor.id, ...next },
            {
              onSuccess: () => {
                setDrawerFor(null);
                toast({ title: "Permissions updated", severity: "success" });
              },
              onError: (e) => {
                const permission = e instanceof ApiError ? (e.permission ?? permissionDetail(e)) : undefined;
                setDrawerError(
                  e instanceof ApiError
                    ? `${e.message}${permission ? ` (${permission})` : ""}`
                    : "The CRM was unreachable.",
                );
              },
            },
          )
        }
      />

      <InviteResellerMemberDialog
        isOpen={inviteOpen}
        resellerName={resellerName}
        matrix={matrixQuery.data}
        onClose={() => setInviteOpen(false)}
        isSubmitting={invite.isPending}
        error={invite.isError && invite.error instanceof ApiError ? invite.error : null}
        onSubmit={async (input) => {
          const result = await invite.mutateAsync(input);
          // A 201 is NOT the same as "fully provisioned": the server writes
          // crm-api first and portal-api second, and reports a portal-api
          // failure in the body. Reading only the status would tell the
          // operator an invite was emailed when it never was.
          if (result.invited) {
            toast({
              title: "Invite sent",
              description: `${input.email} can now set their password and sign in.`,
              severity: "success",
            });
          } else if (result.invite_error_code === INVITE_ERROR_EMAIL_IS_STAFF) {
            // PERMANENT, not a half-write to retry: the address is already
            // an XOP staff identity, and one address cannot be both staff
            // and an external reseller. Offering "Re-send invite" here
            // would send the operator round a loop that always 409s.
            toast({
              title: "That address is an XOP staff account",
              description:
                `${input.email} already signs in as XOP staff, so it can't also be a reseller ` +
                `login. Revoke this row and invite them under a different address — or change ` +
                `the staff account's role in User Management first.`,
              severity: "error",
            });
          } else {
            toast({
              title: "CRM access granted — invite email not sent",
              description:
                `${input.email} is bound to ${resellerName} but has no sign-in ` +
                `account yet, so they can't log in. Use "Re-send invite" on their row to finish. ` +
                (result.invite_error ?? ""),
              severity: "warning",
            });
          }
        }}
      />

      <ConfirmDialog
        isOpen={pendingRevoke !== null}
        onClose={() => setPendingRevoke(null)}
        variant="destructive"
        title="Revoke reseller access?"
        description={
          pendingRevoke
            ? `Removes ${pendingRevoke.email}'s CRM binding and deactivates their sign-in. CRM access stops within one token lifetime. Their history stays for attribution, and you can invite them again later.`
            : ""
        }
        confirmLabel="Revoke access"
        onConfirm={async () => {
          if (!pendingRevoke) return;
          const target = pendingRevoke;
          const result = await revoke.mutateAsync(target.id);
          if (result.account_disabled) {
            toast({
              title: "Access revoked",
              description: `${target.email} can no longer sign in or reach the CRM.`,
              severity: "success",
            });
          } else {
            // The CRM half is what actually denies data access, and it landed —
            // say so plainly instead of reporting a flat failure the operator
            // would retry from scratch.
            toast({
              title: "CRM access revoked — sign-in still active",
              description:
                `${target.email} can no longer reach any CRM data, but their sign-in account ` +
                `could not be deactivated. Try again in a moment. ${result.account_error ?? ""}`,
              severity: "warning",
            });
          }
        }}
      />

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        variant="destructive"
        title="Delete this user?"
        description={
          pendingDelete
            ? `Removes ${pendingDelete.email} from ${selected.name} and deletes their sign-in. ` +
              `They disappear from this list. What they already did in the CRM stays, and you can invite the same address again later.`
            : ""
        }
        confirmLabel="Delete user"
        onConfirm={async () => {
          if (!pendingDelete) return;
          const target = pendingDelete;
          try {
            const result = await remove.mutateAsync(target.id);
            if (result.account_deleted) {
              toast({ title: "User deleted", description: `${target.email} has been removed.`, severity: "success" });
            } else {
              // The CRM half — what reaches data — landed; say so plainly.
              toast({
                title: "Removed from the company — sign-in kept",
                description: `${target.email} can no longer reach this company's data. ${result.account_error ?? ""}`,
                severity: "warning",
              });
            }
          } catch (e) {
            toast({
              title: "Couldn't delete the user",
              description: e instanceof ApiError ? e.message : "The CRM couldn't be reached.",
              severity: "error",
            });
          }
        }}
      />
    </Box>
  );
}

/**
 * Fallback for `ApiError.permission` (which `lib/api.ts` now populates
 * directly from the body's top-level `permission` or
 * `details.upstream_body.permission`). Kept as a belt-and-suspenders read
 * of `e.details` in case a caller ever gets an `ApiError` built by
 * something other than `parseResponse` (e.g. hand-constructed in a test)
 * that didn't set `.permission` itself.
 */
function permissionDetail(e: ApiError): string | undefined {
  const details = e.details as { permission?: unknown; upstream_body?: { permission?: unknown } } | undefined;
  const value = details?.permission ?? details?.upstream_body?.permission;
  return typeof value === "string" ? value : undefined;
}

function ErrorNotice({
  title,
  detail,
  onRetry,
}: {
  title: string;
  detail: string;
  onRetry: () => void;
}): JSX.Element {
  return (
    <Box
      role="alert"
      sx={{
        display: "grid",
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: (t) => alpha(t.palette.error.main, 0.4),
        bgcolor: (t) => alpha(t.palette.error.main, 0.12),
        px: 1.5,
        py: 1.5,
        fontSize: 14,
        color: "error.main",
      }}
    >
      <Box sx={{ fontWeight: 600 }}>{title}</Box>
      <Box sx={{ fontSize: 12 }}>{detail}</Box>
      <Box>
        <Button size="small" variant="text" color="inherit" onClick={onRetry}>
          Retry
        </Button>
      </Box>
    </Box>
  );
}
